import React, { useContext } from "react";
import { DialogProvider } from "./contexts/DialogContext";
import Login from "./pages/Signup/Login";
import "./index.css";
import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import Settings from "./pages/Settings";
import { QueryClient, QueryClientProvider } from "react-query";
import ProtectedRoute from "./routing/ProtectedRoute";
import AdminRoute from "./routing/AdminRoute";
import Billing from "./pages/Settings/Organization/Billing";
import Users from "./pages/Settings/Organization/Users";

import User from "./pages/Settings/User";
import ConfirmPasswordReset from "./pages/Settings/User/ConfirmPasswordReset";
import AppLoading from "./components/EmptyLoadingStates/AppLoading";
import OTPProtectedRoute from "./routing/OTPProtectedRoute";
import SharedShipment from "./pages/Sharing/SharedShipment";
import NewOrganization from "./pages/Settings/Organization/NewOrganization";
import { TeamProvider } from "./contexts/TeamContext";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Onboarding from "./pages/Signup/Onboarding";
import ForceGraph from "./pages/ForceGraph";
import FlowBuilder from "./pages/FlowBuilder";
import ManageWorkflow from "./pages/Workflows/ManageWorkflow";
import PlatformAuthentication from "./pages/PlatformAuthentication";
// core layout
import Layout from "./components/Layouts/Layout";

// workflows
import Workflows from "./pages/Workflows";
import ViewWorkflow from "./pages/Workflows/ViewWorkflow";
import ManageScheduledWorkflowVariant from "./pages/Workflows/ManageScheduledWorkflowVariant";
import ViewScheduledWorkflowVariantRuns from "./pages/Workflows/ViewScheduledWorkflowVariantRuns";
// automations
import Automations from "./pages/Automations";
// credentials
import Credentials from "./pages/Credentials";
import ManageLambda from "./pages/ManageLambda";
import Lambdas from "./pages/Lambdas";
import ManageCredentials from "./pages/Credentials/ManageCredentials";
import { AudioProvider } from "./contexts/AudioContext";
import { SearchProvider } from "./contexts/SearchContext";
import { TenantProvider } from "./contexts/TenantContext";
import NodeEditor from "./pages/NodeEditor/index.mjs";
const queryClient = new QueryClient();

// TODO: Move route defs to a separate file
const router = createBrowserRouter([
  {
    path: "/share/:rectangleId",
    element: (
      <OTPProtectedRoute>
        <SharedShipment />
      </OTPProtectedRoute>
    ),
  },
  {
    path: "/flow",
    element: <NodeEditor />,
  },

  {
    path: "settings",
    element: (
      <ProtectedRoute>
        <Settings />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <User />,
      },
      {
        path: "organization/new",

        element: <NewOrganization />,
      },
      {
        path: "users",
        index: true,
        element: <Users />,
      },
      {
        path: "billing",
        index: true,
        element: <Billing />,
      },
    ],
  },
  { path: "login", element: <Login /> },
  {
    path: "graph",
    element: (
      <ProtectedRoute>
        <ForceGraph />
      </ProtectedRoute>
    ),
  },
  {
    path: "/platform-auth",
    element: (
      <ProtectedRoute>
        <PlatformAuthentication />
      </ProtectedRoute>
    ),
  },
  // home route
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Automations />,
      },
      {
        path: "/workflows",
        element: (
          <AdminRoute>
            <Workflows />
          </AdminRoute>
        ),
      },
      {
        path: "/workflows/:slug",
        element: (
          <AdminRoute>
            <ViewWorkflow />
          </AdminRoute>
        ),
      },
      {
        path: "/workflows/:workflowId?/manage",
        element: (
          <AdminRoute>
            <ManageWorkflow />
          </AdminRoute>
        ),
      },
      {
        path: "/workflows/:workflowId?/scheduled/manage",
        element: (
          <AdminRoute>
            <ManageScheduledWorkflowVariant />
          </AdminRoute>
        ),
      },
      {
        path: "/workflows/:workflowId/scheduled/:scheduledWorkflowId/runs",
        element: (
          <AdminRoute>
            <ViewScheduledWorkflowVariantRuns />
          </AdminRoute>
        ),
      },
      {
        path: "/automations",
        index: true,
        element: <Automations />,
      },
      {
        path: "/credentials",
        element: (
          <AdminRoute>
            <Credentials />
          </AdminRoute>
        ),
      },
      {
        path: "/credentials/:credentialId?/manage",
        element: (
          <AdminRoute>
            <ManageCredentials />
          </AdminRoute>
        ),
      },
      {
        path: "/lambdas",
        element: (
          <AdminRoute>
            <Lambdas />
          </AdminRoute>
        ),
      },
      {
        path: "/lambdas/:lambdaId?/manage",
        element: (
          <ProtectedRoute>
            <ManageLambda />
          </ProtectedRoute>
        ),
      },
    ],
  },

  {
    path: "flowbuilder/:slug",
    element: (
      <ProtectedRoute>
        <FlowBuilder />
      </ProtectedRoute>
    ),
  },
  { path: "auth/reset", element: <ConfirmPasswordReset /> },
]);

function Content() {
  const { loadingAuth } = useContext(AuthContext);

  // If auth is loading, then show app loading state
  if (loadingAuth) return <AppLoading />;

  return (
    <div style={{ height: "100vh" }} className="bg-white">
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <QueryClientProvider client={queryClient}>
        <TeamProvider>
          <AudioProvider>
            <TenantProvider>
              <DialogProvider>
                <SearchProvider>
                  {/* Panel provider handles context for the side panel*/}
                  {/* Move to redux or MobX when app gets more complicated*/}

                  <RouterProvider router={router} />
                </SearchProvider>
              </DialogProvider>
            </TenantProvider>
          </AudioProvider>
        </TeamProvider>
      </QueryClientProvider>
    </div>
  );
}

function App() {
  return (
    // Child components get context passed from parent in tree, so need to wrap
    <AuthProvider>
      <Content />
    </AuthProvider>
  );
}

export default App;
